import React, { useEffect, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";

const ChatGPTNews = () => {
  const [loading, setLoading] = useState(true);
  const [newspaper, setNewspaper] = useState("");
  const [status, setStatus] = useState("Generating your newspaper...");
  const [prevReports, setPrevReports] = useState([]);
  // "today" means we're showing today's newspaper;
  // any other value will be a timestamp string for a previous report.
  const [selectedReportTimestamp, setSelectedReportTimestamp] = useState("today");

  const token = localStorage.getItem("authToken");

  const isReportFromToday = (timestamp) => {
    const savedDate = new Date(Number(timestamp));
    const now = new Date();
    return (
      savedDate.getFullYear() === now.getFullYear() &&
      savedDate.getMonth() === now.getMonth() &&
      savedDate.getDate() === now.getDate()
    );
  };

  const fetchNewspaper = useCallback(
    async (force = false) => {
      setLoading(true);
      try {
        setStatus("Generating your newspaper...");
        const response = await fetch(
          `https://dashboard.plazor.xyz/api/generateNewspaper${force ? "?force=true" : ""}`,
          {
            headers: { Authorization: `Basic ${token}` },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to generate newspaper");
        }
        const data = await response.json();
        setNewspaper(data.newspaper);
        localStorage.setItem("chatGPTNewsReport", data.newspaper);
        localStorage.setItem("chatGPTNewsReportTimestamp", Date.now().toString());
        // Refresh the list of reports after generating today's newspaper.
        fetchPreviousReports();
        // Ensure we’re showing today's report.
        setSelectedReportTimestamp("today");
      } catch (err) {
        console.error("Error generating newspaper:", err);
        setNewspaper("⚠️ Failed to generate today's newspaper.");
      } finally {
        setStatus("");
        setLoading(false);
      }
    },
    [token]
  );

  const fetchPreviousReports = useCallback(async () => {
    try {
      const response = await fetch("https://dashboard.plazor.xyz/api/fetch", {
        headers: { Authorization: `Basic ${token}` },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch reports");
      }
      const data = await response.json();
      if (data.chatgptNews) {
        // Sort reports descending by timestamp.
        const sortedReports = data.chatgptNews.sort((a, b) => b.timestamp - a.timestamp);
        setPrevReports(sortedReports);
      }
    } catch (err) {
      console.error("Error fetching previous reports:", err);
    }
  }, [token]);

  // On mount, load today's newspaper from local storage if available,
  // otherwise fetch it, and always fetch the list of previous reports.
  useEffect(() => {
    const savedReport = localStorage.getItem("chatGPTNewsReport");
    const savedTimestamp = localStorage.getItem("chatGPTNewsReportTimestamp");
    if (savedReport && savedTimestamp && isReportFromToday(savedTimestamp)) {
      setNewspaper(savedReport);
      setLoading(false);
    } else {
      fetchNewspaper();
    }
    fetchPreviousReports();
  }, [fetchNewspaper, fetchPreviousReports]);

  // Determine which newspaper to display.
  const getDisplayedNewspaper = () => {
    if (selectedReportTimestamp === "today") {
      return newspaper;
    } else {
      const report = prevReports.find(
        (r) => r.timestamp.toString() === selectedReportTimestamp
      );
      return report ? report.response : newspaper;
    }
  };

  // Format a timestamp into a readable date string.
  const formatDate = (timestamp) => {
    const date = new Date(Number(timestamp));
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-800 via-blue-600 to-blue-500 text-white p-4 md:p-6">
      <h1 className="text-3xl md:text-4xl font-extrabold mb-4 text-center">
        ChatGPT’s Morning Newspaper 📰💙
      </h1>
      {loading ? (
        <div className="text-center animate-pulse">
          <p className="text-xl">{status}</p>
        </div>
      ) : (
        <>
          <div className="bg-blue-900 p-4 md:p-6 rounded-2xl shadow-xl max-w-4xl mx-auto text-lg leading-relaxed">
            <ReactMarkdown>{getDisplayedNewspaper()}</ReactMarkdown>
          </div>

          {/* Dropdown menu to select which day's newspaper to display */}
          <div className="text-center mt-6">
            <label htmlFor="reportSelect" className="block mb-2">
              Select Newspaper Date:
            </label>
            <select
              id="reportSelect"
              value={selectedReportTimestamp}
              onChange={(e) => setSelectedReportTimestamp(e.target.value)}
              className="p-2 rounded text-black"
            >
              <option value="today">Today's Newspaper</option>
              {prevReports
                .filter((report) => !isReportFromToday(report.timestamp))
                .map((report) => (
                  <option key={report.timestamp} value={report.timestamp.toString()}>
                    {formatDate(report.timestamp)}
                  </option>
                ))}
            </select>
          </div>

          {/* Conditionally show a button based on the selected report */}
          <div className="text-center mt-6">
            {selectedReportTimestamp === "today" ? (
              <button
                onClick={() => fetchNewspaper(true)}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-xl font-bold"
              >
                Regenerate Newspaper
              </button>
            ) : (
              <button
                onClick={() => setSelectedReportTimestamp("today")}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-xl font-bold"
              >
                Back to Today's Newspaper
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatGPTNews;
